import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { eventService } from '../services/eventService';
import { useAuth } from '../context/AuthContext';
import { notifyVisit } from '../services/notifyService';
import { EventResponse, PhotoResponse, GuestInfo } from '../types';
import { useTranslation } from 'react-i18next';
import { translateError } from '../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
`;

const Topbar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 2rem;
  background: #1a1a35;
  border-bottom: 1px solid #2d2d5e;
`;

const BackLink = styled(Link)`
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.9rem;
  &:hover { color: #e2e8f0; }
`;

const Logo = styled.span`
  font-size: 1.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
`;

const EventHeader = styled.div`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const EventTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 1rem 0;
`;

const MetaItem = styled.span`
  font-size: 0.85rem;
  color: #94a3b8;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
  @media (max-width: 600px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const MobileStack = styled.div`
  display: contents;
  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
  }
`;

const IconBtn = styled.button<{ variant?: 'danger' }>`
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
  background: ${p => p.variant === 'danger' ? '#7f1d1d' : '#2d2d5e'};
  color: white;
  display: none;
  align-items: center;
  justify-content: center;
  &:hover { opacity: 0.85; }
  @media (max-width: 600px) { display: flex; }
`;

const DesktopOnly = styled.div`
  display: contents;
  @media (max-width: 600px) { display: none; }
`;

const ActionBtn = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  border: none;
  &:hover { opacity: 0.85; }
  background: ${p => {
    if (p.variant === 'primary') return 'linear-gradient(135deg, #7c3aed, #ec4899)';
    if (p.variant === 'danger') return '#7f1d1d';
    if (p.variant === 'success') return '#065f46';
    return '#2d2d5e';
  }};
  color: ${p => p.variant === 'secondary' ? '#94a3b8' : 'white'};
`;

const AnchorBtn = styled.a`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  background: #2d2d5e;
  color: #e2e8f0;
  &:hover { opacity: 0.85; }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  background: ${p => {
    if (p.status === 'ACTIVE') return 'rgba(16,185,129,0.15)';
    if (p.status === 'EXPIRED') return 'rgba(239,68,68,0.15)';
    if (p.status === 'PENDING') return 'rgba(245,158,11,0.15)';
    return 'rgba(148,163,184,0.15)';
  }};
  color: ${p => {
    if (p.status === 'ACTIVE') return '#10b981';
    if (p.status === 'EXPIRED') return '#ef4444';
    if (p.status === 'PENDING') return '#f59e0b';
    return '#94a3b8';
  }};
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #2d2d5e;
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: 0.6rem 1.25rem;
  background: ${p => p.active ? '#7c3aed' : 'transparent'};
  border: 1px solid ${p => p.active ? '#7c3aed' : 'transparent'};
  border-radius: 8px 8px 0 0;
  color: ${p => p.active ? '#fff' : '#94a3b8'};
  font-weight: ${p => p.active ? '700' : '500'};
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s;
  margin-bottom: -1px;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media (min-width: 600px) { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
  gap: 1rem;
`;

const PhotoCard = styled.div`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 12px;
  overflow: hidden;
`;

const PhotoImg = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
`;

const PhotoInfo = styled.div`
  padding: 0.75rem;
`;

const PhotoName = styled.p`
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0 0 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PhotoActions = styled.div`
  display: flex;
  gap: 0.4rem;
`;

const SmallBtn = styled.button<{ color?: string }>`
  flex: 1;
  padding: 0.3rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.color || '#2d2d5e'};
  color: white;
  transition: opacity 0.2s;
  &:hover { opacity: 0.8; }
`;

const GuestTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #94a3b8;
  border-bottom: 1px solid #2d2d5e;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  border-bottom: 1px solid #1a1a35;
  color: #cbd5e1;
`;

const QRSection = styled.div`
  text-align: center;
  padding: 2rem;
`;

const QRImg = styled.img`
  width: 240px;
  height: 240px;
  background: white;
  border-radius: 12px;
  padding: 12px;
`;

const LightboxNav = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${p => p.side}: 1rem;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.15);
  color: white;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.4rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.3); }
`;

const LightboxCounter = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255,255,255,0.6);
  font-size: 0.85rem;
  z-index: 10;
`;

const Toast = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%) translateY(${p => p.visible ? '0' : '-2rem'});
  background: #fbbf24;
  color: #78350f;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  opacity: ${p => p.visible ? 1 : 0};
  transition: opacity 0.3s, transform 0.3s;
  pointer-events: none;
  z-index: 2000;
  white-space: nowrap;
`;

type ActiveTab = 'gallery' | 'guests' | 'qr';

const AdminEventPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const justCreated = (location.state as any)?.justCreated === true;
  const [event, setEvent] = useState<EventResponse | null>(null);
  const [photos, setPhotos] = useState<PhotoResponse[]>([]);
  const [guests, setGuests] = useState<GuestInfo[]>([]);
  const [tab, setTab] = useState<ActiveTab>(justCreated ? 'qr' : 'gallery');
  const [loading, setLoading] = useState(true);
  const [qrBlobUrl, setQrBlobUrl] = useState<string | null>(null);
  const [toast, setToast] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const touchStartX = useRef<number>(0);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const locale = i18n.language === 'es' ? 'es-MX' : 'en-US';

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const [ev, ph] = await Promise.all([
        eventService.getEvent(Number(eventId)),
        eventService.getEventPhotos(Number(eventId)),
      ]);
      setEvent(ev);
      setPhotos(ph);
      notifyVisit('admin', `Admin: ${admin?.email || ''} | Evento: "${ev.name}" | Código: ${ev.eventCode} | Fotos: ${ph.length}`);
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  }, [eventId, navigate]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  useEffect(() => {
    if (tab === 'guests' && eventId) {
      eventService.getGuests(Number(eventId)).then(setGuests).catch(console.error);
    }
    if (tab === 'qr' && eventId && admin && !qrBlobUrl) {
      const url = eventService.getQRUrl(Number(eventId), admin.sessionToken);
      fetch(url, { headers: { Authorization: `Bearer ${admin.sessionToken}` } })
        .then(r => r.blob())
        .then(blob => setQrBlobUrl(URL.createObjectURL(blob)))
        .catch(console.error);
    }
  }, [tab, eventId, admin, qrBlobUrl]);

  const startEditing = () => {
    setEditName(event?.name || '');
    setEditDesc(event?.description || '');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!eventId || !editName.trim()) return;
    setSaving(true);
    try {
      const updated = await eventService.updateEvent(Number(eventId), editName, editDesc);
      setEvent(updated);
      setEditing(false);
    } catch { alert(t('admin_event.err_save')); }
    finally { setSaving(false); }
  };

  const handleDeleteEvent = async () => {
    if (!eventId) return;
    if (!window.confirm(t('admin_event.confirm_delete'))) return;
    try {
      await eventService.deleteEvent(Number(eventId));
      navigate('/dashboard');
    } catch (e: any) {
      alert(translateError(e, t('admin_event.err_delete')));
    }
  };

  const handleShare = async (url: string, title: string) => {
    const imageUrl = 'https://fotovibe.mx/ficha-informativa.png';
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], 'fotovibe.png', { type: blob.type });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], url, title });
        return;
      }
    } catch {}
    if (navigator.share) {
      navigator.share({ url, title });
    } else {
      navigator.clipboard.writeText(url);
      alert(t('admin_event.copied'));
    }
  };

  const handleToggleUpload = async () => {
    if (!eventId) return;
    const updated = await eventService.toggleUpload(Number(eventId));
    setEvent(updated);
  };

  const handleApprove = async (photoId: number) => {
    await eventService.approvePhoto(photoId);
    setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: 'APPROVED' } : p));
  };

  const handleReject = async (photoId: number) => {
    await eventService.rejectPhoto(photoId);
    setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: 'REJECTED' } : p));
  };

  const handleDelete = async (photoId: number) => {
    if (!window.confirm(t('admin_event.confirm_delete_photo'))) return;
    await eventService.deletePhoto(photoId);
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const downloadZip = () => {
    if (!admin || !eventId) return;
    setToast(true);
    setTimeout(() => setToast(false), 3000);
    const url = eventService.getDownloadUrl(Number(eventId));
    fetch(url, { headers: { Authorization: `Bearer ${admin.sessionToken}` } })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'fotos-evento.zip';
        link.click();
        URL.revokeObjectURL(blobUrl);
      });
  };

  const preloadAdjacent = (idx: number) => {
    [-1, 1, 2].forEach(offset => {
      const p = photos[(idx + offset + photos.length) % photos.length];
      const url = p?.thumbnailUrl || p?.fileUrl;
      if (url) { const img = new Image(); img.src = url; }
    });
  };

  const prevPhoto = () => setLightboxIdx(i => {
    if (i === null) return null;
    const next = (i - 1 + photos.length) % photos.length;
    preloadAdjacent(next);
    return next;
  });
  const nextPhoto = () => setLightboxIdx(i => {
    if (i === null) return null;
    const next = (i + 1) % photos.length;
    preloadAdjacent(next);
    return next;
  });

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      else if (e.key === 'ArrowRight') nextPhoto();
      else if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, photos.length]);

  const handleDownloadPhoto = (photo: { fileUrl: string; originalFilename: string }) => {
    fetch(photo.fileUrl)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = photo.originalFilename || 'foto.jpg';
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  if (loading) return <div style={{ color: '#e2e8f0', padding: '4rem', textAlign: 'center' }}>{t('admin_event.loading')}</div>;
  if (!event) return null;

  const pendingCount = photos.filter(p => p.status === 'PENDING').length;

  const statusLabel = (s: string) => {
    if (s === 'ACTIVE') return t('admin_event.status_active');
    if (s === 'EXPIRED') return t('admin_event.status_expired');
    return s;
  };

  return (
    <Wrapper>
      <Topbar>
        <BackLink to="/dashboard">{t('admin_event.back')}</BackLink>
        <Logo>FotoVibe</Logo>
      </Topbar>

      <Content>
        <EventHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              {editing ? (
                <>
                  <input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    maxLength={120}
                    style={{ width: '100%', background: '#0f0f23', border: '1px solid #7c3aed', borderRadius: 8, padding: '0.5rem 0.75rem', color: '#e2e8f0', fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem', boxSizing: 'border-box' }}
                  />
                  <textarea
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder={t('admin_event.desc_placeholder')}
                    style={{ width: '100%', background: '#0f0f23', border: '1px solid #7c3aed', borderRadius: 8, padding: '0.5rem 0.75rem', color: '#94a3b8', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <ActionBtn variant="primary" onClick={handleSaveEdit} disabled={saving}>{saving ? t('admin_event.saving') : t('admin_event.save')}</ActionBtn>
                    <ActionBtn onClick={() => setEditing(false)}>{t('admin_event.cancel')}</ActionBtn>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div>
                    <EventTitle>{event.name}</EventTitle>
                    {event.description && <p style={{ color: '#94a3b8', margin: '0.25rem 0 0' }}>{event.description}</p>}
                  </div>
                  <button onClick={startEditing} title="Editar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', marginTop: '0.25rem', fontSize: '1.1rem', display: 'inline-block', transform: 'scaleX(-1)' }}>✏️</button>
                </div>
              )}
            </div>
            <StatusBadge status={event.status}>{statusLabel(event.status)}</StatusBadge>
          </div>

          <MetaRow>
            {event.eventDate && (
              <MetaItem>📅 {new Date(event.eventDate).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}</MetaItem>
            )}
            {event.expiresAt && (
              <MetaItem>{t('admin_event.meta_expires')} {new Date(event.expiresAt).toLocaleDateString(locale)}</MetaItem>
            )}
            <MetaItem>📸 {photos.filter(p => p.status === 'APPROVED').length} {t('admin_event.meta_approved')}</MetaItem>
            {pendingCount > 0 && <MetaItem style={{ color: '#f59e0b' }}>⏸ {pendingCount} {t('admin_event.meta_pending')}</MetaItem>}
            {event.createdAt && (
              <MetaItem>{t('admin_event.meta_created')} {new Date(event.createdAt).toLocaleDateString(locale)}</MetaItem>
            )}
          </MetaRow>

          <ActionsRow>
            <MobileStack>
              <ActionBtn variant={event.uploadEnabled ? 'danger' : 'success'} onClick={handleToggleUpload}>
                {event.uploadEnabled ? t('admin_event.btn_pause') : t('admin_event.btn_resume')}
              </ActionBtn>
              <AnchorBtn href={event.publicUrl} target="_blank" rel="noopener noreferrer">
                {t('admin_event.btn_guest_view')}
              </AnchorBtn>
            </MobileStack>

            <DesktopOnly>
              <ActionBtn onClick={downloadZip}>{t('admin_event.btn_zip')}</ActionBtn>
              <AnchorBtn href={event.publicUrl} target="_blank" rel="noopener noreferrer">{t('admin_event.btn_guest_view')}</AnchorBtn>
              {event.publicUrl && (
                <ActionBtn onClick={() => handleShare(event.publicUrl!, event.name)}>{t('admin_event.btn_share')}</ActionBtn>
              )}
              {(event.photoCount === 0) && (
                <ActionBtn variant="danger" onClick={handleDeleteEvent}>{t('admin_event.btn_delete')}</ActionBtn>
              )}
            </DesktopOnly>

            <IconBtn onClick={downloadZip} title={t('admin_event.btn_zip')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </IconBtn>
            {event.publicUrl && (
              <IconBtn onClick={() => handleShare(event.publicUrl!, event.name)} title={t('admin_event.btn_share')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </IconBtn>
            )}
            {(event.photoCount === 0) && (
              <IconBtn variant="danger" onClick={handleDeleteEvent} title={t('admin_event.btn_delete')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </IconBtn>
            )}
          </ActionsRow>
        </EventHeader>

        <Tabs>
          <Tab active={tab === 'gallery'} onClick={() => setTab('gallery')}>
            {t('admin_event.tab_gallery')} {pendingCount > 0 && `(${pendingCount} ${t('admin_event.pending_suffix')})`}
          </Tab>
          <Tab active={tab === 'guests'} onClick={() => setTab('guests')}>
            {t('admin_event.tab_guests')}
          </Tab>
          <Tab active={tab === 'qr'} onClick={() => setTab('qr')}>
            {t('admin_event.tab_qr')}
          </Tab>
        </Tabs>

        {tab === 'gallery' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <ActionBtn onClick={() => eventService.getEventPhotos(Number(eventId)).then(setPhotos)}>
                {t('admin_event.reload')}
              </ActionBtn>
              <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{photos.length} foto{photos.length !== 1 ? 's' : ''}</span>
            </div>
            {photos.length === 0 && (
              <p style={{ color: '#94a3b8', textAlign: 'center', padding: '3rem' }}>
                {t('admin_event.no_photos')}
              </p>
            )}
            <PhotoGrid>
              {photos.map((photo, idx) => (
                <PhotoCard key={photo.id}>
                  <PhotoImg src={photo.thumbnailUrl || photo.fileUrl} alt={photo.originalFilename} loading="lazy" onClick={() => { setLightboxIdx(idx); preloadAdjacent(idx); }} style={{ cursor: 'zoom-in' }} />
                  <PhotoInfo>
                    <PhotoName title={photo.invitadoName || t('admin_event.anonymous')}>
                      {photo.invitadoName || t('admin_event.anonymous')}
                    </PhotoName>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <StatusBadge status={photo.status}>
                        {photo.status === 'PENDING' ? t('admin_event.status_pending') : photo.status === 'APPROVED' ? t('admin_event.status_approved') : t('admin_event.status_rejected')}
                      </StatusBadge>
                    </div>
                    <PhotoActions>
                      {photo.status === 'PENDING' && (
                        <>
                          <SmallBtn color="#065f46" onClick={() => handleApprove(photo.id)}>✓</SmallBtn>
                          <SmallBtn color="#7f1d1d" onClick={() => handleReject(photo.id)}>✗</SmallBtn>
                        </>
                      )}
                      <SmallBtn color="#1e40af" onClick={() => handleDownloadPhoto(photo)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </SmallBtn>
                      {photo.status !== 'PENDING' && (
                        <SmallBtn color="#7f1d1d" onClick={() => handleDelete(photo.id)}>🗑</SmallBtn>
                      )}
                    </PhotoActions>
                  </PhotoInfo>
                </PhotoCard>
              ))}
            </PhotoGrid>
          </>
        )}

        {tab === 'guests' && (
          <div style={{ background: '#1a1a35', borderRadius: '12px', overflow: 'hidden' }}>
            {guests.length === 0 ? (
              <p style={{ color: '#94a3b8', padding: '2rem', textAlign: 'center' }}>
                {t('admin_event.no_guests')}
              </p>
            ) : (
              <GuestTable>
                <thead>
                  <tr>
                    <Th>{t('admin_event.th_name')}</Th>
                    <Th>{t('admin_event.th_email')}</Th>
                    <Th>{t('admin_event.th_photos')}</Th>
                    <Th>{t('admin_event.th_last_upload')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {guests.map((g, i) => (
                    <tr key={i}>
                      <Td>{g.name || t('admin_event.anonymous')}</Td>
                      <Td>{g.email || '—'}</Td>
                      <Td>{g.photoCount}</Td>
                      <Td>{g.lastUpload ? new Date(g.lastUpload).toLocaleString(locale) : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </GuestTable>
            )}
          </div>
        )}

        {tab === 'qr' && (
          <QRSection>
            {qrBlobUrl
              ? <QRImg src={qrBlobUrl} alt="QR del evento" />
              : <p style={{ color: '#475569' }}>{t('admin_event.qr_loading')}</p>
            }
            <p style={{ color: '#94a3b8', margin: '1.5rem 0 0' }}>
              {t('admin_event.qr_desc')}
            </p>
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <AnchorBtn
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!qrBlobUrl) return;
                  const a = document.createElement('a');
                  a.href = qrBlobUrl;
                  a.download = 'qr-fotovibe.png';
                  a.click();
                }}
              >
                {t('admin_event.btn_download_qr')}
              </AnchorBtn>
              <ActionBtn onClick={() => handleShare(event.publicUrl || '', event.name)}>
                {t('admin_event.btn_share_link')}
              </ActionBtn>
            </div>
            <p style={{ color: '#475569', fontSize: '0.85rem', marginTop: '1rem' }}>
              {t('admin_event.public_link')} <a href={event.publicUrl} style={{ color: '#7c3aed' }} target="_blank" rel="noopener noreferrer">{event.publicUrl}</a>
            </p>
          </QRSection>
        )}
      </Content>

      <Toast visible={toast}>⬇ Downloading all files...</Toast>

      {lightboxIdx !== null && photos[lightboxIdx] && (
        <div
          onClick={() => setLightboxIdx(null)}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            if (delta > 50) { e.stopPropagation(); prevPhoto(); }
            else if (delta < -50) { e.stopPropagation(); nextPhoto(); }
          }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
        >
          <button onClick={() => setLightboxIdx(null)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: '1.2rem', cursor: 'pointer', zIndex: 10 }}>✕</button>
          <LightboxCounter>{lightboxIdx + 1} / {photos.length}</LightboxCounter>
          <LightboxNav side="left" onClick={e => { e.stopPropagation(); prevPhoto(); }}>‹</LightboxNav>
          <img src={photos[lightboxIdx].thumbnailUrl || photos[lightboxIdx].fileUrl} alt={t('admin_event.download')} onClick={e => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '86vh', objectFit: 'contain', borderRadius: 8 }} />
          <LightboxNav side="right" onClick={e => { e.stopPropagation(); nextPhoto(); }}>›</LightboxNav>
          <a href={photos[lightboxIdx].fileUrl} download={photos[lightboxIdx].originalFilename || 'foto.jpg'} onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)', background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: 8, padding: '0.5rem 1.2rem', fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none', zIndex: 10, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {t('admin_event.download')}
          </a>
        </div>
      )}
    </Wrapper>
  );
};

export default AdminEventPage;
